'use client';

import { useState, useEffect, useCallback } from 'react';
import { ErrorLogger, ErrorLogEntry } from '@/lib/error-logger';
import Icon from '@/components/ui/AppIcon';

const MonitoringDashboard = () => {
  const [stats, setStats] = useState<ReturnType<typeof ErrorLogger.getStats> | null>(null);
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);
  const [selectedError, setSelectedError] = useState<ErrorLogEntry | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const loadData = useCallback(() => {
    ErrorLogger.seedDemoData();
    const s = ErrorLogger.getStats(24);
    const e = ErrorLogger.getLogs(24).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    setStats(s);
    setErrors(e);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const handleResolve = (id: string) => {
    ErrorLogger.resolveError(id);
    loadData();
    if (selectedError?.id === id) setSelectedError(null);
  };

  const severityColor = (severity: ErrorLogEntry['severity']) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  const severityDot = (severity: ErrorLogEntry['severity']) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const uptimeStatus = stats.uptimePercent >= 99 ? 'Operational' : stats.uptimePercent >= 95 ? 'Degraded' : 'Down';
  const uptimeStatusColor = stats.uptimePercent >= 99 ? 'text-green-600' : stats.uptimePercent >= 95 ? 'text-yellow-600' : 'text-red-600';
  const uptimeBg = stats.uptimePercent >= 99 ? 'bg-green-50 border-green-200' : stats.uptimePercent >= 95 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground">System Monitoring</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Live data · Last 24 hours · Refreshed {lastRefresh.toLocaleTimeString('en-GB')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              autoRefresh
                ? 'bg-green-50 text-green-700 border-green-200' :'bg-surface text-muted-foreground border-border'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-border bg-white hover:bg-surface transition-colors"
          >
            <Icon name="ArrowPathIcon" size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Uptime */}
        <div className={`rounded-xl border p-4 ${uptimeBg}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Uptime</span>
            <Icon name="SignalIcon" size={16} className={uptimeStatusColor} />
          </div>
          <div className={`text-3xl font-black ${uptimeStatusColor}`}>{stats.uptimePercent}%</div>
          <div className={`text-xs font-medium mt-1 ${uptimeStatusColor}`}>{uptimeStatus}</div>
        </div>

        {/* Total Errors */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Errors (24h)</span>
            <Icon name="ExclamationTriangleIcon" size={16} className="text-orange-500" />
          </div>
          <div className="text-3xl font-black text-foreground">{stats.totalErrors}</div>
          <div className="text-xs text-red-500 font-medium mt-1">{stats.criticalErrors} critical</div>
        </div>

        {/* Affected Users */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Affected Users</span>
            <Icon name="UsersIcon" size={16} className="text-blue-500" />
          </div>
          <div className="text-3xl font-black text-foreground">{stats.affectedUsers}</div>
          <div className="text-xs text-muted-foreground mt-1">{stats.unresolvedErrors} unresolved</div>
        </div>

        {/* Avg Response */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Avg Response</span>
            <Icon name="ClockIcon" size={16} className="text-purple-500" />
          </div>
          <div className="text-3xl font-black text-foreground">{stats.avgResponseTime}<span className="text-base font-normal">ms</span></div>
          <div className="text-xs text-muted-foreground mt-1">LCP: {stats.avgLcp}ms</div>
        </div>
      </div>

      {/* Error Breakdown + Component Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Errors by Severity */}
        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="font-heading text-base font-bold text-foreground mb-4">Errors by Severity</h3>
          <div className="space-y-3">
            {(['critical', 'high', 'medium', 'low'] as const).map((sev) => {
              const count = stats.errorsBySeverity[sev] || 0;
              const pct = stats.totalErrors > 0 ? (count / stats.totalErrors) * 100 : 0;
              return (
                <div key={sev}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${severityDot(sev)}`} />
                      <span className="text-sm font-medium capitalize text-foreground">{sev}</span>
                    </div>
                    <span className="text-sm font-bold text-foreground">{count}</span>
                  </div>
                  <div className="w-full bg-surface rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${severityDot(sev)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Errors by Component */}
        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="font-heading text-base font-bold text-foreground mb-4">Errors by Component</h3>
          {Object.keys(stats.errorsByComponent).length === 0 ? (
            <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">No errors recorded</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(stats.errorsByComponent)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 6)
                .map(([comp, count]) => (
                  <div key={comp} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <span className="text-sm font-mono text-foreground truncate max-w-[200px]">{comp}</span>
                    <span className="text-sm font-bold text-primary ml-2">{count}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Log Table */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-heading text-base font-bold text-foreground">Recent Error Log</h3>
          <span className="text-xs text-muted-foreground">{errors.length} entries</span>
        </div>

        {errors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <Icon name="CheckCircleIcon" size={24} className="text-green-500" />
            </div>
            <p className="text-sm font-medium text-foreground">No errors in the last 24 hours</p>
            <p className="text-xs text-muted-foreground mt-1">System is running smoothly</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Component</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Message</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Severity</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Users</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {errors.map((err) => (
                  <tr
                    key={err.id}
                    className={`hover:bg-surface/50 transition-colors cursor-pointer ${
                      err.resolved ? 'opacity-50' : ''
                    }`}
                    onClick={() => setSelectedError(selectedError?.id === err.id ? null : err)}
                  >
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatTime(err.timestamp)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-surface px-1.5 py-0.5 rounded">{err.componentName || '—'}</span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <span className="truncate block text-xs text-foreground">{err.message}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${severityColor(err.severity)}`}>
                        {err.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-center font-medium">{err.affectedUsers}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${err.resolved ? 'text-green-600' : 'text-orange-500'}`}>
                        {err.resolved ? 'Resolved' : 'Open'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!err.resolved && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleResolve(err.id); }}
                          className="text-xs text-primary hover:text-secondary font-medium transition-colors"
                        >
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Error Detail Panel */}
      {selectedError && (
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-heading text-base font-bold text-foreground">Error Detail</h3>
            <button onClick={() => setSelectedError(null)} className="text-muted-foreground hover:text-foreground">
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Component</p>
              <p className="text-sm font-mono font-medium">{selectedError.componentName || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Timestamp</p>
              <p className="text-sm font-medium">{formatDate(selectedError.timestamp)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">User Action</p>
              <p className="text-sm font-medium">{selectedError.userAction || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Affected Users</p>
              <p className="text-sm font-medium">{selectedError.affectedUsers}</p>
            </div>
          </div>
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-1">Error Message</p>
            <p className="text-sm font-medium text-red-600">{selectedError.message}</p>
          </div>
          {selectedError.stack && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Stack Trace</p>
              <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all">
                {selectedError.stack}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonitoringDashboard;
